import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { openSupportChat } from '@/utils/supportChat';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Performance', to: '/performance' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Get EA', to: '/get-ea' },
  { label: 'FAQ', to: '/faq' },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !menuOpen;

  const linkBase = 'relative text-sm font-medium transition-colors duration-200 whitespace-nowrap';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/80'
      }`}
    >
      <nav className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          {/* Brand */}
          <Link to="/" className="shrink-0 group">
            <img
              src={transparent ? "/image/foot_logo.png" : "/image/logo.png"}
              alt="GoldWarden EA"
              className="h-20 w-auto transition-opacity duration-300"
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `${linkBase} px-3 lg:px-4 py-2 rounded-full ${
                    isActive
                      ? transparent
                        ? 'text-accent-300'
                        : 'text-primary-600'
                      : transparent
                        ? 'text-background-50/85 hover:text-background-50'
                        : 'text-foreground-700 hover:text-foreground-950'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openSupportChat}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-accent-500 hover:bg-accent-600 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-customer-service-2-line text-base" aria-hidden="true"></i>
              Get EA Free
            </button>

            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                transparent
                  ? 'border-background-50/30 text-background-50'
                  : 'border-background-300 text-foreground-900'
              }`}
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Scroll progress */}
      <div
        className={`h-0.5 w-full origin-left bg-accent-500 transition-opacity duration-300 ${
          transparent ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      ></div>

      {/* Mobile panel */}
      <div
        className={`md:hidden overflow-hidden border-t border-background-200 bg-background-50 transition-[max-height,opacity] duration-300 ${
          menuOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={openSupportChat}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 px-5 py-3 text-sm font-semibold text-primary-950 cursor-pointer"
          >
            <i className="ri-customer-service-2-line text-base" aria-hidden="true"></i>
            Get EA Free
          </button>
        </div>
      </div>
    </header>
  );
}